import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { BookOpen, ArrowRight, Search, Loader2, HeartPulse, Pill, Stethoscope, Leaf, Droplet, Activity, Apple, Moon, Syringe, Bone, Droplets, FlaskConical } from "lucide-react";
import { getLearnIndex } from "../lib/api";
import { useT, useLang, SUPPORTED } from "../lib/i18n";

const SECTION_KEY = {
  Conditions: "sec_conditions",
  Medications: "sec_medications",
  "Tests & Procedures": "sec_tests",
  Wellness: "sec_wellness",
};

const SECTION_ICONS = {
  Conditions: HeartPulse,
  Medications: Pill,
  "Tests & Procedures": Stethoscope,
  Wellness: Leaf,
};

const CARD_ICON_RULES = [
  [["water", "hydrat", "drink"], Droplet],
  [["exercise", "physical activity", "activ"], Activity],
  [["eat", "diet", "nutrition", "food"], Apple],
  [["sleep"], Moon],
  [["vaccine", "vaccinat", "immun"], Syringe],
  [["sciatica", "back", "joint", "bone", "arthritis", "gout"], Bone],
  [["blood sugar", "a1c", "diabetes"], Droplets],
  [["cholesterol", "blood pressure", "heart", "atrial"], HeartPulse],
  [["mri", "ct", "scan", "x ray", "ultrasound", "test", "blood sugar", "psa", "testosterone"], FlaskConical],
  [["side effect", "metformin", "sildenafil", "medication", "drug", "pill"], Pill],
];

function cardIcon(s) {
  const t = ` ${s.slug} ${s.title} ${s.question} `.toLowerCase().replace(/-/g, " ");
  for (const [keys, Icon] of CARD_ICON_RULES) {
    if (keys.some((k) => t.includes(k))) return Icon;
  }
  const SecIcon = SECTION_ICONS[s.section];
  return SecIcon || BookOpen;
}

export default function LearnIndexPage() {
  const t = useT();
  const { lang, setLang } = useLang();
  const { lang: routeLang } = useParams();
  const [query, setQuery] = useState("");
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);

  // Derive the effective language synchronously from the URL so topic-card titles
  // are fetched in the right language on first render (no English-first flash/bleed).
  const pageLang = routeLang && SUPPORTED.includes(routeLang) ? routeLang : lang;

  useEffect(() => {
    if (pageLang !== lang) setLang(pageLang);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageLang]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    getLearnIndex(pageLang)
      .then((data) => { if (active) setPages(data); })
      .catch(() => {})
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [pageLang]);

  const q = query.trim().toLowerCase();
  const filtered = q
    ? pages.filter(
        (s) => s.title.toLowerCase().includes(q) || s.slug.replace(/-/g, " ").includes(q)
      )
    : pages;

  const SECTION_ORDER = ["Conditions", "Medications", "Tests & Procedures", "Wellness"];
  const grouped = {};
  filtered.forEach((s) => {
    const sec = s.section || "Conditions";
    (grouped[sec] = grouped[sec] || []).push(s);
  });
  const rank = (x) => (SECTION_ORDER.indexOf(x) === -1 ? 999 : SECTION_ORDER.indexOf(x));
  const sections = Object.keys(grouped).sort((a, b) => rank(a) - rank(b));

  const Card = (s) => {
    const CardIcon = cardIcon(s);
    return (
      <Link
        key={s.slug}
        to={pageLang && pageLang !== "en" ? `/${pageLang}/${s.slug}` : `/${s.slug}`}
        data-testid={`library-card-${s.slug}`}
        className="group flex items-center gap-3 bg-white border-2 border-slate-200 hover:border-teal-400 hover:bg-teal-50 rounded-2xl p-5 transition-all min-h-[72px]"
      >
        <span className="grid place-items-center w-10 h-10 rounded-xl bg-teal-100 text-teal-700 shrink-0 group-hover:bg-teal-200 transition-colors">
          <CardIcon className="w-5 h-5" />
        </span>
        <span className="flex-1 min-w-0">
          <span className="block font-heading font-bold text-lg text-slate-900">{s.title}</span>
          {s.blurb && (
            <span className="block mt-0.5 text-sm text-slate-500 leading-snug" data-testid={`library-card-blurb-${s.slug}`}>
              {s.blurb}
            </span>
          )}
        </span>
        <ArrowRight className="w-5 h-5 text-teal-500 group-hover:translate-x-1 transition-transform shrink-0" />
      </Link>
    );
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="flex items-center gap-3 mb-2">
        <span className="grid place-items-center w-11 h-11 rounded-xl bg-teal-100 text-teal-700">
          <BookOpen className="w-6 h-6" />
        </span>
        <h1 className="font-heading text-3xl font-extrabold text-slate-900">
          {t("lib_title")}
        </h1>
      </div>
      <p className="text-lg text-slate-500 mb-6">
        {t("lib_subtitle")}
      </p>

      <div className="relative mb-8">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t("lib_search_placeholder")}
          data-testid="library-search-input"
          className="w-full bg-white border-2 border-slate-200 focus:border-teal-500 focus:ring-4 focus:ring-teal-100 rounded-2xl pl-12 pr-4 py-4 text-lg text-slate-900 placeholder:text-slate-400 outline-none transition-all min-h-[60px]"
        />
      </div>

      {loading ? (
        <div className="py-16 text-center">
          <Loader2 className="w-9 h-9 text-teal-600 animate-spin mx-auto" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border-2 border-slate-200 rounded-2xl p-8 text-center" data-testid="library-no-results">
          <p className="text-lg text-slate-600">
            {t("lib_no_results")}
          </p>
        </div>
      ) : (
        <div className="space-y-8">
          {sections.map((sec) => {
            const SecIcon = SECTION_ICONS[sec] || BookOpen;
            return (
              <div key={sec} data-testid={`library-section-${sec.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <h2 className="font-heading text-xl font-bold text-slate-900 mb-3 flex items-center gap-2">
                  <span className="grid place-items-center w-8 h-8 rounded-lg bg-teal-100 text-teal-700 shrink-0">
                    <SecIcon className="w-5 h-5" />
                  </span>
                  {t(SECTION_KEY[sec] || "sec_conditions")}
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {grouped[sec].map((s) => Card(s))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
